import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Drawer, Tabs, Toolbar } from '@mui/material';
import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { filterRoles, hasRole, hasRouteAccess, useIsAuthenthicated } from '../auth';
import LinkTab from '../link-tab/LinkTab';
import { MenuTabProps } from '../menu/Menu';
import { UserRole } from '../types';
import { Pages } from '../types/pages';
import { useActiveCompany } from '../utils/useCompany';

export interface MenuDrawerProps {
  onItemClick: () => void;
  open: boolean;
}

const mobileTabsStyle = {
  '.MuiTabs-scroller': { overflow: 'auto !important' },
  '.MuiTabs-flexContainer': { flexDirection: 'column', gap: 4 },
  '.MuiTabs-indicator': { display: 'none' },
  textTransform: 'uppercase',
};

const MenuDrawer = ({ onItemClick, open }: MenuDrawerProps) => {
  const isAuthenticated = useIsAuthenthicated();
  const { pathname } = useLocation();
  const company = useActiveCompany();
  const routeHidden = !hasRouteAccess();

  const authTabs: MenuTabProps[] = [
    { label: 'Learn', to: Pages.Learn, roles: [UserRole.Student] },
    { label: 'Teach', to: Pages.Teach, roles: [UserRole.Instructor] },
    { label: 'Home', to: '/home', roles: [UserRole.Member] },
    { label: 'Simulators', href: '/v2/i/simulators', roles: [UserRole.Instructor] },
    { label: 'Exercises', href: '/v2/i/exercise', roles: [UserRole.Instructor] },
    company?.roles.includes(UserRole.Instructor)
      ? {
          label: 'Routes',
          key: 'Routes Instructor',
          hidden: routeHidden,
          href: '/v2/i/routes',
          roles: [UserRole.Instructor],
        }
      : {
          label: 'Routes',
          key: 'Routes Student',
          hidden: routeHidden,
          href: '/v2/s/routes',
          roles: [UserRole.Student],
        },
    { label: 'Users', href: '/v2/i/user-and-groups', roles: [UserRole.Instructor] },
    { label: 'Cloud based simulators', href: '/v2/i/marketplace', roles: [UserRole.Instructor, UserRole.Member] },
    {
      label: 'Ship models',
      href: '/v2/i/shipmodel',
      roles: ['Instructor'],
    },
    { label: 'Academy', href: '/v2/i/academy', roles: [UserRole.AreaDeveloper] },

    { label: 'Help center', to: '/help-center', roles: [] },
    { label: 'Profile', to: '/profile', roles: [] },
  ].filter(tab => !tab.hidden && company && hasRole(tab.roles, filterRoles(company?.roles)));

  const unauthTabs: MenuTabProps[] = [{ label: 'Login', to: '/login' }];
  const selectedTabs = (isAuthenticated ? authTabs : unauthTabs).map(
    tab => matchPath({ path: (tab.to ?? tab.href)!, end: false }, pathname)?.pathname,
  );
  const selectedTab = selectedTabs.find(Boolean) ?? false;

  const MenuTab = (props: MenuTabProps & { value: string }) => <LinkTab {...props} onClick={onItemClick} />;

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      PaperProps={{
        sx: { backgroundColor: 'surfaces.background', justifyContent: 'center', width: '100%' },
      }}
      sx={{
        visibility: { xs: 'visible', sm: 'hidden' },
      }}
    >
      <Toolbar />

      <AuthenticatedTemplate>
        <Tabs centered sx={mobileTabsStyle} value={selectedTab}>
          {authTabs.map(({ roles, ...tab }) => (
            <MenuTab key={tab.label} {...tab} value={(tab.to ?? tab.href)!} />
          ))}
        </Tabs>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Tabs centered sx={mobileTabsStyle} value={selectedTab}>
          {unauthTabs.map(tab => (
            <MenuTab key={tab.label} {...tab} value={(tab.to ?? tab.href)!} />
          ))}
        </Tabs>
      </UnauthenticatedTemplate>
    </Drawer>
  );
};

export default MenuDrawer;
